import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const PRIORIDAD = {
  urgente: { color: '#EF4444', bg: 'rgba(239,68,68,0.12)', label: 'Urgente' },
  alta:    { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', label: 'Alta' },
  normal:  { color: '#00E5FF', bg: 'rgba(0,229,255,0.1)',  label: 'Normal' },
  baja:    { color: '#6B7280', bg: 'rgba(107,114,128,0.1)', label: 'Baja' },
};

const ESTADO = {
  abierto:    { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  label: 'Abierto',     icon: '🔴' },
  en_proceso: { color: '#6366F1', bg: 'rgba(99,102,241,0.1)',  label: 'En proceso',  icon: '🔵' },
  resuelto:   { color: '#10B981', bg: 'rgba(16,185,129,0.1)',  label: 'Resuelto',    icon: '✅' },
  cerrado:    { color: '#6B7280', bg: 'rgba(107,114,128,0.1)', label: 'Cerrado',     icon: '⚫' },
  cancelado:  { color: '#EF4444', bg: 'rgba(239,68,68,0.1)',   label: 'Cancelado',   icon: '❌' },
};

const TIPO_LABEL = {
  falla: '⚡ Falla', instalacion: '🔧 Instalación', mantenimiento: '🛠 Mantenimiento',
  retiro: '📦 Retiro', visita_tecnica: '👷 Visita técnica', otro: '📋 Otro',
};

const formVacio = { cliente_id:'', titulo:'', descripcion:'', tipo:'falla', prioridad:'normal', tecnico_id:'', fecha_programada:'', notas_internas:'' };

export default function Tickets() {
  const { usuario } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [buscar, setBuscar] = useState('');
  const [modal, setModal] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [ticketActivo, setTicketActivo] = useState(null);
  const [comentario, setComentario] = useState('');
  const [form, setForm] = useState(formVacio);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  // Lo que trae el técnico de la calle: las fotos y la firma de quien recibió el trabajo.
  const [mirando, setMirando] = useState(null);
  const [firma, setFirma] = useState(null);

  const cargar = useCallback(async () => {
    const params = new URLSearchParams();
    if (filtroEstado) params.append('estado', filtroEstado);
    if (filtroPrioridad) params.append('prioridad', filtroPrioridad);
    if (buscar) params.append('buscar', buscar);
    const [resT, resS] = await Promise.all([
      api.get(`/api/tickets?${params}`),
      api.get('/api/tickets/stats/resumen'),
    ]);
    setTickets(resT.data);
    setStats(resS.data);
  }, [filtroEstado, filtroPrioridad, buscar]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/api/clientes').then(r => setClientes(r.data));
    api.get('/api/usuarios/tecnicos').then(r => setTecnicos(r.data)).catch(() => {});
  }, []);

  const abrirNuevo = () => { setForm(formVacio); setError(''); setModal(true); };

  const guardar = async () => {
    if (!form.titulo) { setError('El título es requerido'); return; }
    setGuardando(true);
    try {
      await api.post('/api/tickets', form);
      setModal(false); cargar();
    } catch (err) { setError(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const verDetalle = async (t) => {
    const { data } = await api.get(`/api/tickets/${t.id}`);
    setTicketActivo(data);
    setComentario('');
    setFirma(null);
    setMirando(null);
    setModalDetalle(true);
  };

  /* Las fotos y la firma se piden de a una y solo al tocarlas: vienen dentro del texto de la
     respuesta y traerlas todas al abrir el ticket haría esperar por algo que casi nunca se
     mira. Por eso el ticket trae la LISTA de fotos y no su contenido. */
  const verFoto = async (foto) => {
    setMirando({ cargando: true });
    try {
      const { data } = await api.get(`/api/tickets/${ticketActivo.id}/fotos/${foto.id}`);
      setMirando({ src: `data:${data.mime_type};base64,${data.foto_base64}`, nombre: data.nombre });
    } catch (err) { setMirando(null); alert('No se pudo abrir la foto'); }
  };

  const verFirma = async () => {
    try {
      const { data } = await api.get(`/api/tickets/${ticketActivo.id}/firma`);
      setFirma(data);
    } catch (err) { alert('No se pudo abrir la firma'); }
  };

  const quitarFirma = async () => {
    if (!window.confirm('¿Quitar la firma de este ticket? El cliente tendría que firmar otra vez.')) return;
    try {
      await api.delete(`/api/tickets/${ticketActivo.id}/firma`);
      setFirma(null);
      const { data } = await api.get(`/api/tickets/${ticketActivo.id}`);
      setTicketActivo(data);
    } catch (err) { alert(err.response?.data?.error || 'No se pudo quitar la firma'); }
  };

  const cambiarEstado = async (nuevoEstado) => {
    try {
      const { data } = await api.put(`/api/tickets/${ticketActivo.id}`, { estado: nuevoEstado });
      setTicketActivo(prev => ({ ...prev, estado: data.estado, fecha_cierre: data.fecha_cierre }));
      cargar();
    } catch (err) { alert('Error al cambiar estado'); }
  };

  const enviarComentario = async (tipo = 'comentario') => {
    if (!comentario.trim()) return;
    try {
      await api.post(`/api/tickets/${ticketActivo.id}/seguimiento`, { comentario, tipo });
      const { data } = await api.get(`/api/tickets/${ticketActivo.id}`);
      setTicketActivo(data);
      setComentario('');
      if (tipo === 'solucion') { cambiarEstado('resuelto'); }
    } catch (err) { alert('Error al enviar comentario'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Layout>
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Tickets de Soporte</h1>
          <p style={s.sub}>Gestión de fallas, instalaciones y visitas técnicas</p>
        </div>
        {usuario?.rol !== 'tecnico' && (
          <button style={s.btnPrimario} onClick={abrirNuevo}>+ Nuevo Ticket</button>
        )}
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        {[
          { label:'Abiertos',   val: stats.abiertos  || 0, color:'#F59E0B', icon:'🔴' },
          
          { label:'Resueltos',  val: stats.resueltos  || 0, color:'#10B981', icon:'✅' },
          { label:'Urgentes',   val: stats.urgentes   || 0, color:'#EF4444', icon:'🚨' },
        ].map(st => (
          <div key={st.label} style={s.statCard}>
            <span style={{ fontSize:'24px' }}>{st.icon}</span>
            <span style={{ ...s.statVal, color: st.color }}>{st.val}</span>
            <span style={s.statLabel}>{st.label}</span>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={s.filtros}>
        <input style={s.buscador} placeholder="🔍  Buscar por título, número o cliente..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        <select style={s.select} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select style={s.select} value={filtroPrioridad} onChange={e => setFiltroPrioridad(e.target.value)}>
          <option value="">Todas las prioridades</option>
          {Object.entries(PRIORIDAD).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Lista de tickets */}
      <div style={s.lista}>
        {tickets.length === 0 ? (
          <div style={s.vacio}>No hay tickets con los filtros aplicados</div>
        ) : tickets.map(t => {
          const pCfg = PRIORIDAD[t.prioridad];
          const eCfg = ESTADO[t.estado];
          return (
            <div key={t.id} style={s.ticketCard} onClick={() => verDetalle(t)}>
              <div style={s.ticketTop}>
                <div style={s.ticketNumTipo}>
                  <span style={s.ticketNum}>{t.numero}</span>
                  <span style={s.ticketTipo}>{TIPO_LABEL[t.tipo]}</span>
                </div>
                <div style={s.ticketBadges}>
                  <span style={{ ...s.badge, color: pCfg.color, background: pCfg.bg }}>{pCfg.label}</span>
                  <span style={{ ...s.badge, color: eCfg.color, background: eCfg.bg }}>{eCfg.icon} {eCfg.label}</span>
                </div>
              </div>
              <div style={s.ticketTitulo}>{t.titulo}</div>
              {t.cliente_nombre && <div style={s.ticketCliente}>👤 {t.cliente_nombre} {t.cliente_telefono ? `· 📞 ${t.cliente_telefono}` : ''}</div>}
              {t.cliente_direccion && <div style={s.ticketDir}>📍 {t.cliente_direccion}</div>}
              <div style={s.ticketFooter}>
                <span style={s.ticketFecha}>📅 {new Date(t.creado_en).toLocaleDateString('es-CO')}</span>
                {t.fecha_programada && <span style={s.ticketFechaProg}>⏰ Programado: {new Date(t.fecha_programada).toLocaleString('es-CO')}</span>}
                {t.tecnico_nombre
                  ? <span style={s.ticketTecnico}>🔧 {t.tecnico_nombre}</span>
                  : <span style={s.sinTecnico}>Sin técnico asignado</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal nuevo ticket */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo="Nuevo Ticket" ancho="580px">
        <div style={s.formGrid}>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Título *</label>
            <input style={s.input} value={form.titulo} onChange={e => f('titulo',e.target.value)} placeholder="Describe el problema brevemente" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Tipo</label>
            <select style={s.input} value={form.tipo} onChange={e => f('tipo',e.target.value)}>
              {Object.entries(TIPO_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Prioridad</label>
            <select style={s.input} value={form.prioridad} onChange={e => f('prioridad',e.target.value)}>
              {Object.entries(PRIORIDAD).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Cliente</label>
            <select style={s.input} value={form.cliente_id} onChange={e => f('cliente_id',e.target.value)}>
              <option value="">Sin cliente específico</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Técnico asignado</label>
            <select style={s.input} value={form.tecnico_id} onChange={e => f('tecnico_id',e.target.value)}>
              <option value="">Sin asignar</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Fecha programada</label>
            <input style={s.input} type="datetime-local" value={form.fecha_programada} onChange={e => f('fecha_programada',e.target.value)} />
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Descripción detallada</label>
            <textarea style={{ ...s.input, minHeight:'80px', resize:'vertical' }} value={form.descripcion} onChange={e => f('descripcion',e.target.value)} placeholder="Describe el problema con detalle..." />
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Notas internas</label>
            <input style={s.input} value={form.notas_internas} onChange={e => f('notas_internas',e.target.value)} placeholder="Notas solo visibles para el equipo" />
          </div>
        </div>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={() => setModal(false)}>Cancelar</button>
          <button style={s.btnPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Crear Ticket'}</button>
        </div>
      </Modal>

      {/* Modal detalle ticket */}
      {ticketActivo && (
        <Modal abierto={modalDetalle} onCerrar={() => setModalDetalle(false)} titulo={`${ticketActivo.numero} — ${ticketActivo.titulo}`} ancho="640px">
          {/* Info del ticket */}
          <div style={s.detalleHeader}>
            <div style={s.detalleBadges}>
              <span style={{ ...s.badge, color: PRIORIDAD[ticketActivo.prioridad]?.color, background: PRIORIDAD[ticketActivo.prioridad]?.bg }}>{PRIORIDAD[ticketActivo.prioridad]?.label}</span>
              <span style={{ ...s.badge, color: ESTADO[ticketActivo.estado]?.color, background: ESTADO[ticketActivo.estado]?.bg }}>{ESTADO[ticketActivo.estado]?.icon} {ESTADO[ticketActivo.estado]?.label}</span>
              <span style={s.badgeNeutro}>{TIPO_LABEL[ticketActivo.tipo]}</span>
            </div>
          </div>

          {ticketActivo.descripcion && <p style={s.detalleDesc}>{ticketActivo.descripcion}</p>}

          <div style={s.detalleInfo}>
            {ticketActivo.cliente_nombre && <div style={s.detalleItem}><span>👤 Cliente:</span><strong>{ticketActivo.cliente_nombre} · {ticketActivo.cliente_telefono}</strong></div>}
            {ticketActivo.cliente_direccion && <div style={s.detalleItem}><span>📍 Dirección:</span><span>{ticketActivo.cliente_direccion}</span></div>}
            {ticketActivo.tecnico_nombre && <div style={s.detalleItem}><span>🔧 Técnico:</span><strong>{ticketActivo.tecnico_nombre}</strong></div>}
            {ticketActivo.fecha_programada && <div style={s.detalleItem}><span>⏰ Programado:</span><span>{new Date(ticketActivo.fecha_programada).toLocaleString('es-CO')}</span></div>}
          </div>

          {/* Cambiar estado. «En proceso» se ofrece desde que la base lo acepta: antes estaba
              en esta pantalla y en el contador, y la base lo rechazaba. */}
          {ticketActivo.estado !== 'cerrado' && ticketActivo.estado !== 'cancelado' && ticketActivo.estado !== 'resuelto' && (
            <div style={s.estadoBtns}>
              {ticketActivo.estado === 'abierto' && <button style={{ ...s.btnEstado, background:'rgba(99,102,241,0.15)', color:'#A5B4FC' }} onClick={() => cambiarEstado('en_proceso')}>🔵 En proceso</button>}
              <button style={{ ...s.btnEstado, background:'rgba(16,185,129,0.15)', color:'#6EE7B7' }} onClick={() => cambiarEstado('resuelto')}>✅ Marcar resuelto</button>
              {['admin','superadmin'].includes(usuario?.rol) && <button style={{ ...s.btnEstado, background:'rgba(107,114,128,0.15)', color:'#9CA3AF' }} onClick={() => cambiarEstado('cerrado')}>⚫ Cerrar ticket</button>}
            </div>
          )}

          {/* ── Lo que trajo el técnico de la calle ──
              Sin esto, el técnico escribiría la solución, tomaría fotos y haría firmar al
              cliente, y desde la oficina no se vería nada de eso. */}
          {(ticketActivo.solucion || ticketActivo.resuelto_por_nombre) && (
            <div style={s.trabajoHecho}>
              <div style={s.trabajoTitulo}>
                🔧 Qué hizo el técnico
                {/* Quién lo RESOLVIÓ, que no es quién lo tenía asignado: desde que todos los
                    técnicos ven todos los tickets, un trabajo que empezó uno lo puede cerrar
                    otro, y la fila de arriba seguiría mostrando al primero. */}
                {ticketActivo.resuelto_por_nombre && (
                  <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                    {' '}· lo resolvió <strong>{ticketActivo.resuelto_por_nombre}</strong>
                  </span>
                )}
              </div>
              {ticketActivo.solucion
                ? <p style={s.trabajoTexto}>{ticketActivo.solucion}</p>
                : <p style={{ ...s.trabajoTexto, color: 'rgba(255,255,255,0.45)' }}>
                    Se marcó como resuelto sin escribir qué se hizo.
                  </p>}
            </div>
          )}

          {!!ticketActivo.fotos?.length && (
            <>
              <div style={s.seguimientoTitulo}>📷 Fotos del trabajo ({ticketActivo.fotos.length})</div>
              <div style={s.fotosFila}>
                {ticketActivo.fotos.map(fo => (
                  <button key={fo.id} style={s.fotoChip} onClick={() => verFoto(fo)}>
                    <span style={{ fontSize:'20px' }}>🖼️</span>
                    <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>
                      {new Date(fo.creado_en).toLocaleDateString('es-CO', { day:'numeric', month:'short' })}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {ticketActivo.tiene_firma && (
            <>
              <div style={s.seguimientoTitulo}>✍️ Firma del cliente</div>
              <div style={s.firmaCaja}>
                <div>
                  <strong style={{ fontSize:'13px' }}>{ticketActivo.firma_nombre}</strong>
                  <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>
                    {ticketActivo.firma_en && new Date(ticketActivo.firma_en).toLocaleString('es-CO')}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'8px' }}>
                  {!firma && <button style={s.btnComentario} onClick={verFirma}>Ver firma</button>}
                  {['admin','superadmin'].includes(usuario?.rol) && (
                    <button style={{ ...s.btnComentario, background:'rgba(239,68,68,0.12)', color:'#FCA5A5' }} onClick={quitarFirma}>Quitar</button>
                  )}
                </div>
              </div>
              {firma?.firma_base64 && (
                <img src={`data:image/png;base64,${firma.firma_base64}`} alt="Firma del cliente"
                     style={{ width:'100%', maxWidth:'320px', background:'#fff', borderRadius:'10px', marginTop:'10px' }} />
              )}
            </>
          )}

          {/* Seguimiento */}
          <div style={s.seguimientoTitulo}>💬 Seguimiento</div>
          <div style={s.seguimientoLista}>
            {ticketActivo.seguimiento?.length === 0 && <div style={s.sinSeguimiento}>Sin comentarios aún</div>}
            {ticketActivo.seguimiento?.map(seg => (
              <div key={seg.id} style={{ ...s.segItem, ...(seg.tipo === 'solucion' ? s.segSolucion : {}) }}>
                <div style={s.segHeader}>
                  <strong style={{ fontSize:'13px' }}>{seg.usuario_nombre}</strong>
                  <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>{new Date(seg.creado_en).toLocaleString('es-CO')}</span>
                  {seg.tipo !== 'comentario' && <span style={s.segTipoBadge}>{seg.tipo === 'solucion' ? '✅ Solución' : '📋 Actualización'}</span>}
                </div>
                <p style={s.segTexto}>{seg.comentario}</p>
              </div>
            ))}
          </div>

          {/* Agregar comentario */}
          {ticketActivo.estado !== 'cerrado' && (
            <div style={s.comentarioArea}>
              <textarea style={s.comentarioInput} value={comentario} onChange={e => setComentario(e.target.value)} placeholder="Escribe un comentario o actualización..." rows={3} />
              <div style={s.comentarioBtns}>
                <button style={s.btnComentario} onClick={() => enviarComentario('comentario')}>💬 Comentar</button>
                <button style={{ ...s.btnComentario, background:'rgba(16,185,129,0.15)', color:'#6EE7B7' }} onClick={() => enviarComentario('solucion')}>✅ Marcar como solución</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* La foto en grande. Fuera del Modal del ticket para que quede encima de él. */}
      {mirando && (
        <div onClick={() => setMirando(null)} style={s.visor}>
          {mirando.cargando
            ? <span style={{ color:'rgba(255,255,255,0.6)' }}>Abriendo la foto…</span>
            : <img src={mirando.src} alt={mirando.nombre || 'Foto del trabajo'} style={s.visorImg} />}
        </div>
      )}
    </Layout>
  );
}

const s = {
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom:'24px' },
  titulo: { fontSize:'24px', fontWeight:'700', marginBottom:'4px' },
  sub: { color:'rgba(255,255,255,0.4)', fontSize:'14px' },
  btnPrimario: { background:'linear-gradient(135deg,#00E5FF,#6366F1)', border:'none', borderRadius:'10px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  btnSec: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 18px', color:'#fff', fontSize:'14px', cursor:'pointer' },
  statsRow: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:'12px', marginBottom:'20px' },
  statCard: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'12px', padding:'16px', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px' },
  statVal: { fontSize:'28px', fontWeight:'700' },
  statLabel: { fontSize:'12px', color:'rgba(255,255,255,0.4)' },
  filtros: { display:'flex', flexWrap: 'wrap', gap:'10px', marginBottom:'16px' },
  buscador: { flex: '1 1 220px', minWidth: 0, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'9px 16px', color:'#fff', fontSize:'14px', outline:'none' },
  select: { maxWidth: '100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'9px 14px', color:'#fff', fontSize:'13px', outline:'none', cursor:'pointer' },
  lista: { display:'flex', flexDirection:'column', gap:'10px' },
  vacio: { padding:'60px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'14px' },
  ticketCard: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'14px', padding:'16px 20px', cursor:'pointer', transition:'border-color 0.2s' },
  ticketTop: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' },
  ticketNumTipo: { display:'flex', alignItems:'center', gap:'10px' },
  ticketNum: { fontSize:'12px', fontWeight:'700', color:'#00E5FF', fontFamily:'monospace' },
  ticketTipo: { fontSize:'12px', color:'rgba(255,255,255,0.4)' },
  ticketBadges: { display:'flex', gap:'6px' },
  badge: { fontSize:'11px', fontWeight:'600', padding:'3px 10px', borderRadius:'20px' },
  badgeNeutro: { fontSize:'11px', color:'rgba(255,255,255,0.5)', background:'rgba(255,255,255,0.06)', padding:'3px 10px', borderRadius:'20px' },
  ticketTitulo: { fontSize:'15px', fontWeight:'600', marginBottom:'6px' },
  ticketCliente: { fontSize:'12px', color:'rgba(255,255,255,0.6)', marginBottom:'2px' },
  ticketDir: { fontSize:'12px', color:'rgba(255,255,255,0.4)', marginBottom:'8px' },
  ticketFooter: { display:'flex', gap:'16px', alignItems:'center', flexWrap:'wrap' },
  ticketFecha: { fontSize:'11px', color:'rgba(255,255,255,0.4)' },
  ticketFechaProg: { fontSize:'11px', color:'#F59E0B' },
  ticketTecnico: { fontSize:'11px', color:'#A5B4FC' },
  sinTecnico: { fontSize:'11px', color:'rgba(255,255,255,0.25)', fontStyle:'italic' },
  formGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' },
  campo: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { color:'rgba(255,255,255,0.6)', fontSize:'13px' },
  input: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'10px 14px', color:'#fff', fontSize:'14px', outline:'none' },
  error: { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px', marginBottom:'16px' },
  modalFooter: { display:'flex', gap:'12px', justifyContent:'flex-end' },
  detalleHeader: { marginBottom:'12px' },
  detalleBadges: { display:'flex', gap:'8px', flexWrap:'wrap' },
  detalleDesc: { fontSize:'14px', color:'rgba(255,255,255,0.7)', lineHeight:'1.6', background:'rgba(255,255,255,0.03)', borderRadius:'8px', padding:'12px', marginBottom:'14px' },
  detalleInfo: { display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' },
  detalleItem: { display:'flex', gap:'10px', fontSize:'13px', color:'rgba(255,255,255,0.7)' },
  estadoBtns: { display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' },

  // Lo que llega de la calle: la solución escrita, las fotos y la firma.
  trabajoHecho: { background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.22)', borderRadius:'10px', padding:'12px 14px', marginBottom:'16px' },
  trabajoTitulo: { fontSize:'12px', fontWeight:'700', color:'#6EE7B7', marginBottom:'6px', letterSpacing:'0.04em' },
  trabajoTexto: { margin:0, fontSize:'14px', lineHeight:1.6, whiteSpace:'pre-wrap', overflowWrap:'anywhere' },
  fotosFila: { display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'16px' },
  fotoChip: { width:'72px', height:'72px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.04)', cursor:'pointer', display:'grid', placeItems:'center', gap:'2px', color:'#fff' },
  firmaCaja: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'12px 14px' },
  visor: { position:'fixed', inset:0, zIndex:2000, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px', cursor:'zoom-out' },
  visorImg: { maxWidth:'100%', maxHeight:'90vh', borderRadius:'12px' },
  btnEstado: { background:'rgba(99,102,241,0.15)', border:'none', borderRadius:'8px', padding:'8px 14px', color:'#A5B4FC', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  seguimientoTitulo: { fontSize:'13px', fontWeight:'600', color:'rgba(255,255,255,0.6)', marginBottom:'10px' },
  seguimientoLista: { maxHeight:'200px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' },
  sinSeguimiento: { fontSize:'13px', color:'rgba(255,255,255,0.3)', textAlign:'center', padding:'16px' },
  segItem: { background:'rgba(255,255,255,0.03)', borderRadius:'8px', padding:'10px 14px' },
  segSolucion: { background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.2)' },
  segHeader: { display:'flex', gap:'10px', alignItems:'center', marginBottom:'6px' },
  segTipoBadge: { fontSize:'10px', background:'rgba(16,185,129,0.1)', color:'#6EE7B7', padding:'1px 6px', borderRadius:'10px' },
  segTexto: { fontSize:'13px', color:'rgba(255,255,255,0.7)', lineHeight:'1.5' },
  comentarioArea: { borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:'14px' },
  comentarioInput: { width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'10px', color:'#fff', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box', marginBottom:'8px' },
  comentarioBtns: { display:'flex', gap:'8px' },
  btnComentario: { background:'rgba(99,102,241,0.15)', border:'none', borderRadius:'8px', padding:'7px 14px', color:'#A5B4FC', fontSize:'12px', cursor:'pointer', fontWeight:'600' },
};
